/**
 * quizGate – may the teacher move the class on to the next topic?
 *
 * The rule the teacher asked for is simple: move on only when every student has
 * finished the quiz. What makes it hard is deciding what "finished" means from
 * the progress docs students write about themselves (`progress/{uid}`), because
 * the obvious signal is the wrong one.
 *
 * `phase === 'completed'` used to be read as done. It is not tied to a topic, so
 * a student who finished the previous topic's quiz and had not yet swapped still
 * looked finished with the next one. And it was not only the student who could
 * put them there: the teacher's lockstep broadcast `completed` too, dropping the
 * whole class onto "Lesson Complete" with their answers unsent (see
 * `isFollowablePhase` in ./phases). Phase says where a student is, not what they
 * have handed in.
 *
 * So doneness comes from the hand-in itself. When a student hands in a quiz the
 * player writes `quiz_topic_id` (which topic it was for) and `quiz_complete`
 * (true for the final hand-in, false for the partial one sent when the class
 * moves on mid-quiz). A student is done with this topic when both say so. Once
 * that is true it stays true, whatever the phase does afterwards: a teacher who
 * takes the class back to the intro to go over something does not reopen the
 * gate and make everyone sit the quiz again.
 *
 * Tabs opened before that code shipped write neither. For them
 * `phase === 'completed'` is still the best signal there is, so it is kept as a
 * fallback rather than locking the teacher out of their own class for one stale
 * tab. But a missing `quiz_topic_id` is not enough to tell an old tab: a new tab
 * has none until its first hand-in, and on a topic with no questions it reaches
 * `completed` without ever handing one in. Read as done, that doc was done with
 * whatever topic came next — finish a quiz-less topic A, the teacher moves to B,
 * and until each tab reported again the whole class looked finished with B, so
 * a quick second press of Next skipped B's quiz unseen. What does tell an old
 * tab is `topic_id`, which a new tab writes on every report and an old one never
 * does. So the fallback applies only when both fields are missing, and a new tab
 * counts only by handing in. (One on a topic with no quiz loses nothing by that:
 * the gate is open for the topic itself.)
 *
 * Who counts is everyone with a progress doc, less students the teacher removed
 * and students who have left. A student who comes back has `left_at` cleared on
 * arrival and counts again. Guests in partner sessions write progress docs too,
 * and are counted on purpose: they are sitting the lesson.
 *
 * The gate is open with nobody in the room, when the topic has no quiz, and when
 * everyone is done. While the host is still loading the topic's questions it
 * cannot know which, and `'unknown'` is treated as having a quiz: open-by-default
 * there would let a quick second press of Next skip a topic mid-swap, before
 * anyone had seen its quiz. Closed-by-default costs nothing — the teacher can
 * still force past it — and it resolves as soon as the questions arrive.
 *
 * This is a gate on the teacher's controls, not on the data. Students report
 * their own progress and the rules do not count docs, so it works on trust; it
 * exists to stop a teacher leaving half the class mid-quiz by accident, not to
 * stop one who means to.
 *
 * Nothing here reads Firestore or resolves a display name. The caller passes the
 * docs it already has and a `nameOf` callback, which keeps this module free of
 * firebase and of `utils/displayName`, and testable on plain objects.
 */

/**
 * The fields of a student's progress doc the gate reads. `SessionStudentProgress`
 * fits it; so does a plain object in a test.
 */
export interface GateProgress {
  student_uid?: string | null;
  phase: string;
  removed?: boolean | null;
  /** Any truthy value — a Firestore Timestamp or an ISO string — means gone. */
  left_at?: unknown;
  /** The topic the student is on as of their last report. Old tabs never write it. */
  topic_id?: string | null;
  /** The topic whose quiz this student last handed in, if any. */
  quiz_topic_id?: string | null;
  /** True for the final hand-in; false for a partial one sent on moving on. */
  quiz_complete?: boolean | null;
}

/** A student the gate is waiting for, ready to list in the Force next dialog. */
export interface GateStudent {
  uid: string;
  name: string;
  /** Where they are now, so the teacher can see "in quiz" from "still loading". */
  phase: string;
}

/** Why the gate is open, or `'waiting'` when it is not. */
export type QuizGateReason = 'no-students' | 'no-quiz' | 'all-done' | 'waiting';

export interface QuizGate {
  /** Counted students who have handed in this topic's quiz. */
  done: number;
  /** Students who count: present, not removed, not left. */
  total: number;
  /** True when the teacher may move on without forcing. */
  open: boolean;
  reason: QuizGateReason;
  /**
   * Every counted student who is not done, sorted by name. It always holds
   * `total - done` students whatever the gate says; the caller shows it only
   * while the gate is closed.
   */
  unfinished: GateStudent[];
}

export interface QuizGateInput<P extends GateProgress = GateProgress> {
  progress: ReadonlyArray<P>;
  /** The topic the class is on now. */
  currentTopicId: string;
  /** `'unknown'` while the topic's questions are still loading; counts as true. */
  topicHasQuiz: boolean | 'unknown';
  /** Display name for a student, e.g. from `resolveStudentDisplayName`. */
  nameOf: (p: P) => string;
}

/** Present, and neither removed by the teacher nor gone. */
function counts(p: GateProgress): boolean {
  return Boolean(p.student_uid) && !p.removed && !p.left_at;
}

/** Handed in this topic's quiz — or, in a tab too old to write either topic field, reached `completed`. */
function hasHandedIn(p: GateProgress, currentTopicId: string): boolean {
  if (p.quiz_topic_id == null) return p.topic_id == null && p.phase === 'completed';
  return p.quiz_topic_id === currentTopicId && p.quiz_complete === true;
}

/**
 * Names as a person would sort a register: ignoring case and accents, and with
 * "Guest 2" before "Guest 10". The uid breaks ties so the order never flickers
 * between snapshots when two students share a name.
 */
function byName(a: GateStudent, b: GateStudent): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }) || (a.uid < b.uid ? -1 : a.uid > b.uid ? 1 : 0);
}

export function evaluateQuizGate<P extends GateProgress>(input: QuizGateInput<P>): QuizGate {
  const { progress, currentTopicId, topicHasQuiz, nameOf } = input;

  const students: P[] = progress.filter(counts);
  const unfinished: GateStudent[] = students
    .filter((p) => !hasHandedIn(p, currentTopicId))
    .map((p) => ({ uid: String(p.student_uid), name: nameOf(p), phase: p.phase }))
    .sort(byName);

  const total: number = students.length;
  const done: number = total - unfinished.length;

  let reason: QuizGateReason;
  if (total === 0) reason = 'no-students';
  else if (topicHasQuiz === false) reason = 'no-quiz';
  else if (done === total) reason = 'all-done';
  else reason = 'waiting';

  return { done, total, open: reason !== 'waiting', reason, unfinished };
}
